'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { RouterOutputs } from '@package/api'
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, toast } from '@ui/web/components'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'

interface DeleteBookCollectionModalProps {
  data: NonNullable<RouterOutputs['myBookCollection']['getOneBookCollection']>
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  className?: string
}

export function DeleteBookCollectionModal({
  data,
  open,
  onOpenChange,
  onSuccess,
  className,
}: DeleteBookCollectionModalProps) {
  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const deleteBookCollectionMutation = useMutation(
    trpc.myBookCollection.deleteBookCollection.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.myBookCollection.getAllBookCollections.queryKey(),
        })
        toast.success('Berhasil menghapus Koleksi')
        onOpenChange(false)
        onSuccess()
      },
    }),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('srd:w-full srd:max-w-[500px]', className)}>
        <DialogHeader>
          <DialogTitle className='srd:sr-only'>Hapus Koleksi {data.name}?</DialogTitle>

          <DialogDescription className='srd:sr-only'>
            Koleksi yang dihapus tidak bisa dikembalikan. Apakah kamu yakin?
          </DialogDescription>
        </DialogHeader>

        <div className='srd:flex srd:justify-center srd:items-center srd:text-center srd:flex-col srd:gap-y-2'>
          <img src={getStaticAssetUrl('illustration/foam.png')} alt='' width={60} height={60} draggable={false} />

          <h6 className='srd:text-lg srd:font-semibold'>Hapus Koleksi {data.name}?</h6>
          <p className='srd:text-sm'>Koleksi yang dihapus tidak bisa dikembalikan. Apakah kamu yakin?</p>
        </div>

        <div className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full srd:mt-4'>
          <Button
            variant='destructive'
            loading={deleteBookCollectionMutation.isPending}
            disabled={deleteBookCollectionMutation.isPending}
            onClick={() => {
              deleteBookCollectionMutation.mutate({
                id: data.id,
              })
            }}
          >
            Ya, hapus Koleksi
          </Button>

          <Button variant='ghost' disabled={deleteBookCollectionMutation.isPending} onClick={() => onOpenChange(false)}>
            Tidak jadi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
